import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsService } from '../services/analytics/AnalyticsService';
import { AnalyticsConfig, AnalyticsUser } from '../services/analytics/types';
import { ConsentService } from '../services/consent/ConsentService';
import { marketingConfigPublica } from '../Utils/Services';

type Ctx = {
  analytics: AnalyticsService;
  config: AnalyticsConfig | undefined;
  /** Carrega config de uma loja específica (chamado quando dadosEmpresa muda). */
  carregarConfig: (empresaId: string) => void;
  /** Identifica usuário corrente. */
  identificar: (user: AnalyticsUser | undefined) => void;
};

const AnalyticsContext = createContext<Ctx | null>(null);

type ProviderProps = {
  children: ReactNode;
};

/**
 * Provider central da camada de Analytics.
 *
 * - Instancia AnalyticsService único
 * - Reage a mudanças de consentimento
 * - Dispara PageView automático em mudanças de rota (Ionic + react-router)
 *
 * Para carregar config de uma loja, chame ctx.carregarConfig(empresaId) quando
 * `dadosEmpresa.empresaid` (ou `loja`) for conhecido.
 */
export const AnalyticsProvider: React.FC<ProviderProps> = ({ children }) => {
  const service = useMemo(() => new AnalyticsService(), []);
  const [config, setConfig] = useState<AnalyticsConfig | undefined>(undefined);
  const lastLoadedEmpresa = useRef<string | undefined>(undefined);
  const location = useLocation();
  const lastPath = useRef<string | undefined>(undefined);

  // Reage a consent change → tenta inicializar adapters
  useEffect(() => {
    const unsub = ConsentService.subscribe(() => {
      service.onConsentChanged();
    });
    return () => {
      unsub();
    };
  }, [service]);

  // PageView automático em troca de rota (SPA)
  useEffect(() => {
    if (!config || !config.ativo) return;
    if (config.rastreamentoAutomatico === false) return;
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    service.page({ extras: { page_path: location.pathname } });
  }, [location.pathname, config, service]);

  const carregarConfig = (empresaId: string) => {
    if (!empresaId || lastLoadedEmpresa.current === empresaId) return;
    lastLoadedEmpresa.current = empresaId;
    const url = marketingConfigPublica.url.replace('{id}', String(empresaId).replaceAll('#', '%23'));
    fetch(url, { method: marketingConfigPublica.method })
      .then((r) => r.json())
      .then((resp) => {
        const item = (resp && resp.Item) || {};
        const cfg: AnalyticsConfig = { ...item, empresaId };
        setConfig(cfg);
        service.setConfig(cfg);
      })
      .catch((err) => {
        console.warn('[AnalyticsProvider] erro ao carregar config', err);
      });
  };

  const identificar = (user: AnalyticsUser | undefined) => {
    service.setUser(user);
  };

  const value: Ctx = { analytics: service, config, carregarConfig, identificar };
  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

export function useAnalyticsContext(): Ctx {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    throw new Error('useAnalyticsContext deve ser usado dentro de <AnalyticsProvider>');
  }
  return ctx;
}

export default AnalyticsProvider;
